// Settings.jsx
// Enterprise-grade Settings surface for SurFox
// Single scrollable surface, accordion sections, real-time status

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Shield,
  Phone,
  PhoneCall,
  MessageSquare,
  Globe,
  Users,
  Settings as SettingsIcon,
  ChevronDown,
  CheckCircle,
  Plug,
  Bell,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettingsData } from '../components/settings/surfox-settings/hooks/useSettingsData';

// Section Content Components
import CompanyProfileSection from '../components/settings/surfox-settings/sections/CompanyProfileSection';
import A2PRegistration from '../components/settings/A2PRegistration'; // Your locked component
import PhoneNumbersSection from '../components/settings/surfox-settings/sections/PhoneNumbersSection';
import MessagingSection from '../components/settings/surfox-settings/sections/MessagingSection';
import WebChatSection from '../components/settings/surfox-settings/sections/WebChatSection';
import VoiceChannelSection from '../components/settings/surfox-settings/sections/VoiceChannelSection';
import MessengerSection from '../components/settings/surfox-settings/sections/MessengerSection';
import TeamSection from '../components/settings/surfox-settings/sections/TeamSection';
import SystemSection from '../components/settings/surfox-settings/sections/SystemSection';
import IntegrationsSection from '../components/settings/surfox-settings/sections/IntegrationsSection';
import OutboundWebhooksSection from '../components/settings/surfox-settings/sections/OutboundWebhooksSection';
import PushNotificationSettings from '../components/settings/PushNotificationSettings';

// ============================================================================
// SETTINGS SECTION COMPONENT
// ============================================================================

const SettingsSection = ({ 
  id,
  icon: Icon,
  iconGradient,
  title,
  status,
  statusText,
  summary,
  lastUpdated,
  quickAction,
  onQuickAction,
  progress,
  avatars,
  isExpanded,
  onToggle,
  children,
  tourClass
}) => {
  
  const getStatusBadge = () => {
    switch (status) {
      case 'complete':
        return (
          <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
            <span className="hidden xs:inline">{statusText || 'Complete'}</span>
            <span className="xs:hidden">✓</span>
          </span>
        );
      case 'attention':
        return (
          <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <span className="relative flex h-1.5 w-1.5 mr-1 sm:mr-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
            </span>
            <span className="hidden sm:inline">{statusText || 'Action Required'}</span>
            <span className="sm:hidden">Action</span>
          </span>
        );
      case 'healthy':
        return (
          <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
            <span className="hidden xs:inline">{statusText || 'Healthy'}</span>
            <span className="xs:hidden">✓</span>
          </span>
        );
      case 'count':
        return (
          <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 text-slate-600">
            {statusText}
          </span>
        );
      default:
        return null;
    }
  };

  return (
<motion.div
  data-tour={id}
  className={`
    bg-white rounded-xl sm:rounded-2xl border shadow-sm overflow-hidden
    transition-colors duration-200
    ${status === 'attention' ? 'border-amber-200' : 'border-slate-200'}
    ${isExpanded ? 'shadow-lg' : 'hover:shadow-md'}
    ${(id === 'company' || id === 'phones') && isExpanded ? 'animate-tour-pulse' : ''}
  `}
>
      {/* Section Header */}
      <button
        onClick={onToggle}
        data-tour={`${id}-header`}
        className="w-full px-3 sm:px-6 py-3 sm:py-5 text-left..."
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            <div className={`
              w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center shadow-sm flex-shrink-0
              bg-gradient-to-br ${iconGradient}
            `}>
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
                <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{title}</h3>
                {getStatusBadge()}
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">{summary}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {/* Avatars for Team section - hidden on mobile */}
            {avatars && avatars.length > 0 && (
              <div className="hidden sm:flex -space-x-2">
                {avatars.slice(0, 3).map((avatar, i) => (
                  <div
                    key={i}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium ${avatar.color}`}
                  >
                    {avatar.initials}
                  </div>
                ))}
                {avatars.length > 3 && (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-300 border-2 border-white flex items-center justify-center text-slate-600 text-xs font-medium">
                    +{avatars.length - 3}
                  </div>
                )}
              </div>
            )}
            
            {/* Quick Action Button - hidden on mobile when collapsed */}
            {quickAction && !isExpanded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickAction?.();
                }}
                className={`
                  hidden sm:block px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors
                  ${status === 'attention' 
                    ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' 
                    : 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                  }
                `}
              >
                {quickAction}
              </button>
            )}
            
            {/* Last Updated - hidden on mobile and tablet */}
            {lastUpdated && !isExpanded && (
              <span className="text-xs text-slate-400 hidden xl:inline">
                {lastUpdated}
              </span>
            )}
            
            {/* Chevron */}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            </motion.div>
          </div>
        </div>
        
        {/* Progress Bar (for A2P) */}
        {progress && !isExpanded && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
              <span className="text-slate-600 font-medium">Registration Progress</span>
              <span className="text-slate-500">{progress.current}/{progress.total}</span>
            </div>
            <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(progress.current / progress.total) * 100}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            {/* Steps - simplified on mobile */}
            <div className="hidden sm:flex justify-between mt-2 text-xs text-slate-400">
              {progress.steps.map((step, i) => (
                <span 
                  key={i}
                  className={
                    step.complete ? 'text-emerald-600 font-medium' :
                    step.current ? 'text-amber-600 font-medium' : ''
                  }
                >
                  {step.complete ? '✓ ' : step.current ? '⏳ ' : ''}{step.label}
                </span>
              ))}
            </div>
            {/* Mobile: show only current step */}
            <div className="sm:hidden mt-2 text-xs text-slate-500">
              {progress.steps.find(s => s.current)?.label || 'In Progress'}
            </div>
          </div>
        )}
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="px-3 sm:px-6 pb-4 sm:pb-6 border-t border-slate-100">
              <div className="pt-4 sm:pt-6">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================================================
// QUICK STATS BAR
// ============================================================================

const QuickStatsBar = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
      <div className="bg-white rounded-lg sm:rounded-xl border border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide">Phones</div>
        <div className="mt-0.5 sm:mt-1 flex items-baseline space-x-1 sm:space-x-2">
          <span className="text-lg sm:text-xl font-semibold text-slate-900">{stats.phoneNumbers}</span>
          <span className="text-xs sm:text-sm text-slate-500">active</span>
        </div>
      </div>
      
      <div className="bg-white rounded-lg sm:rounded-xl border border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide">Team</div>
        <div className="mt-0.5 sm:mt-1 flex items-baseline space-x-1 sm:space-x-2">
          <span className="text-lg sm:text-xl font-semibold text-slate-900">{stats.teamMembers}</span>
          <span className="text-xs sm:text-sm text-slate-500">users</span>
        </div>
      </div>
      
      <div className="bg-white rounded-lg sm:rounded-xl border border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide">A2P</div>
        <div className="mt-0.5 sm:mt-1 flex items-center space-x-1.5 sm:space-x-2">
          {stats.a2pStatus === 'approved' ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium text-emerald-600">Verified</span>
            </>
          ) : stats.a2pStatus === 'pending' ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium text-amber-600">Pending</span>
            </>
          ) : (
            <>
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium text-slate-600">Setup</span>
            </>
          )}
        </div>
      </div>
      
    </div>
  );
};

// ============================================================================
// ACCOUNT HEALTH RING
// ============================================================================

const AccountHealthRing = ({ percentage, actionsNeeded }) => {
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference * (1 - percentage / 100);
  
  return (
    <div className="flex items-center space-x-2 sm:space-x-4">
      <div className="text-right">
        <div className="text-xs sm:text-sm font-medium text-slate-900">Health</div>
        <div className="text-[10px] sm:text-xs text-slate-500">
          {actionsNeeded > 0 ? `${actionsNeeded} action${actionsNeeded > 1 ? 's' : ''}` : 'All set'}
        </div>
      </div>
      <div className="relative w-10 h-10 sm:w-12 sm:h-12">
        <svg className="w-10 h-10 sm:w-12 sm:h-12 transform -rotate-90">
          <circle 
            cx="20" 
            cy="20" 
            r="18" 
            stroke="#e2e8f0" 
            strokeWidth="3" 
            fill="none" 
            className="sm:hidden"
          />
          <motion.circle 
            cx="20" 
            cy="20" 
            r="18" 
            stroke="#EA580C" 
            strokeWidth="3" 
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="sm:hidden"
          />
          <circle 
            cx="24" 
            cy="24" 
            r="20" 
            stroke="#e2e8f0" 
            strokeWidth="4" 
            fill="none" 
            className="hidden sm:block"
          />
          <motion.circle 
            cx="24" 
            cy="24" 
            r="20" 
            stroke="#EA580C" 
            strokeWidth="4" 
            fill="none"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 20}
            initial={{ strokeDashoffset: 2 * Math.PI * 20 }}
            animate={{ strokeDashoffset: (2 * Math.PI * 20) * (1 - percentage / 100) }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="hidden sm:block"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs sm:text-sm font-semibold text-slate-900">{percentage}%</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN SETTINGS COMPONENT
// ============================================================================

const Settings = () => {
  const { user, loading: authLoading } = useAuth();
  const [expandedSection, setExpandedSection] = useState(null);
  const location = useLocation();

  // Sections flagged `globalAdminOnly` are hidden from customers and shown only
  // to us. Not ready for self-serve yet — flip the flag off to release one.
  const isGlobalAdmin = user?.role === 'global_admin' || user?.user_metadata?.role === 'global_admin';

  // Deep-link support: /settings?section=<id> auto-opens that card (e.g. the
  // Get-Live Companion's A2P/company links). Sections are components on this
  // single page, not separate routes.
  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (section) setExpandedSection(section);
  }, [location.search]);

  // Fetch all settings data
  const { 
    data, 
    loading: dataLoading, 
    refetch 
  } = useSettingsData(user?.tenant_id);

  useEffect(() => {
    document.title = "Settings — SurFox";
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Calculate account health
  const calculateHealth = () => {
    let score = 0;
    let total = 5;
    let actions = 0;
    
    if (data.company?.isComplete) score++;
    else actions++;
    
    if (data.a2p?.status === 'approved') score++;
    else if (data.a2p?.status !== 'approved') actions++;
    
    if (data.phones?.total > 0) score++;
    else actions++;
    
    if (data.messaging?.isConfigured) score++;
    
    if (data.team?.total > 0) score++;
    
    return {
      percentage: Math.round((score / total) * 100),
      actionsNeeded: actions
    };
  };

  const health = calculateHealth();

  // Listen for tour section expansion requests
useEffect(() => {
  const handleTourExpand = (event) => {
    const { sectionId } = event.detail;
    console.log('🎯 Settings: Tour requested expand:', sectionId);
    setExpandedSection(sectionId);
  };
  
  window.addEventListener('tour-expand-section', handleTourExpand);
  return () => window.removeEventListener('tour-expand-section', handleTourExpand);
}, []);

  // Section definitions
  const sections = [
    {
      id: 'company',
      icon: Building2,
      iconGradient: 'from-blue-500 to-blue-600',
      title: 'Company Profile',
      status: data.company?.isComplete ? 'complete' : 'attention',
      statusText: data.company?.isComplete ? 'Complete' : 'Incomplete',
      summary: data.company?.summary || 'Business identity and operating hours',
      lastUpdated: data.company?.lastUpdated,
      component: CompanyProfileSection,
      tourClass: 'tour-company-profile'
    },
    {
      id: 'a2p',
      icon: Shield,
      iconGradient: 'from-amber-500 to-orange-500',
      title: 'A2P Compliance',
      status: data.a2p?.status === 'approved' ? 'complete' : 'attention',
      statusText: data.a2p?.status === 'approved' ? 'Verified' : 'Action Required',
      summary: data.a2p?.summary || 'Brand registration and messaging campaigns',
      quickAction: data.a2p?.status !== 'approved' ? 'Complete Setup →' : null,
      progress: data.a2p?.status !== 'approved' ? data.a2p?.progress : null,
      component: A2PRegistration
    },
    {
      id: 'phones',
      icon: Phone,
      iconGradient: 'from-violet-500 to-purple-600',
      title: 'Phone Numbers',
      status: data.phones?.total > 0 ? 'healthy' : 'attention',
      statusText: data.phones?.total > 0 ? 'Healthy' : 'No Numbers',
      summary: data.phones?.summary || 'SMS numbers and assignments',
      quickAction: '+ Add Number',
      component: PhoneNumbersSection,
      tourClass: 'tour-phone-numbers'
    },
    {
      id: 'messaging',
      icon: MessageSquare,
      iconGradient: 'from-emerald-500 to-teal-600',
      title: 'Messaging',
      status: 'healthy',
      statusText: 'Optimized',
      summary: data.messaging?.summary || 'Response timing and TCPA compliance',
      component: MessagingSection
    },
    {
      id: 'web-chat',
      icon: Globe,
      iconGradient: 'from-cyan-500 to-blue-600',
      title: 'Web Channel',
      status: data.webChat?.isConfigured ? 'healthy' : 'attention',
      statusText: data.webChat?.isConfigured ? 'Active' : 'Not Installed',
      summary: data.webChat?.summary || 'Chat widget for your website',
      quickAction: data.webChat?.isConfigured ? null : 'Get Snippet →',
      component: WebChatSection
    },
    {
      id: 'voice-channel',
      icon: PhoneCall,
      iconGradient: 'from-violet-500 to-purple-600',
      title: 'Voice Channel',
      status: data.voiceChannel?.isConfigured ? 'healthy' : 'attention',
      statusText: data.voiceChannel?.isConfigured ? 'Active' : 'No Number',
      summary: data.voiceChannel?.summary || 'AI voice agent for inbound calls',
      quickAction: data.voiceChannel?.isConfigured ? null : 'Claim Number →',
      component: VoiceChannelSection
    },
    {
      id: 'messenger',
      icon: MessageSquare,
      iconGradient: 'from-blue-500 to-indigo-600',
      title: 'Messenger Channel',
      status: data.messenger?.isConfigured ? 'healthy' : 'attention',
      statusText: data.messenger?.isConfigured ? 'Connected' : (data.messenger?.pageStatus ? 'Needs attention' : 'Not Connected'),
      summary: data.messenger?.summary || 'AI replies to your Facebook Page messages',
      quickAction: data.messenger?.isConfigured ? null : 'Connect Page →',
      component: MessengerSection
    },
    {
      id: 'push-notifications',
      icon: Bell,
      iconGradient: 'from-rose-500 to-red-600',
      title: 'Push Notifications',
      status: null,
      summary: 'Browser alerts when leads go hot',
      component: PushNotificationSettings
    },
    {
      id: 'team',
      icon: Users,
      iconGradient: 'from-pink-500 to-rose-600',
      title: 'Team',
      status: 'count',
      statusText: `${data.team?.total || 0} members`,
      summary: data.team?.summary || 'Members and permissions',
      avatars: data.team?.avatars || [],
      component: TeamSection
    },
    {
      id: 'integrations',
      icon: Plug, // or Link2 from lucide-react
      iconGradient: 'from-indigo-500 to-blue-600',
      title: 'Integrations',
      status: data.integrations?.connected > 0 ? 'count' : null,
      statusText: data.integrations?.connected > 0 ? `${data.integrations.connected} connected` : null,
      summary: 'Zapier · HighLevel · Follow Up Boss · CRM connections',
      component: IntegrationsSection
    },
    {
      id: 'outbound-webhooks',
      icon: Plug,
      iconGradient: 'from-orange-500 to-amber-600',
      title: 'Outbound Webhooks',
      status: null,
      summary: 'HighLevel · Salesforce · Podio · Bullhorn · custom webhook URLs',
      component: OutboundWebhooksSection
    },
    {
      id: 'system',
      icon: SettingsIcon,
      iconGradient: 'from-slate-600 to-slate-700',
      title: 'System',
      status: null,
      summary: 'Data exports · Audit logs · Advanced tools',
      component: SystemSection,
      globalAdminOnly: true
    }
  ];

  // Loading state
  if (authLoading || dataLoading) {
    return (
      <div className="min-h-[50vh] sm:min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3 sm:space-y-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 sm:border-3 border-slate-200 border-t-slate-600 animate-spin" />
          <p className="text-xs sm:text-sm text-slate-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 antialiased">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-10">
        
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-0.5 sm:mb-1">
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">Settings</h1>
                {data.tenant?.name && (
                  <span className="hidden sm:inline-block px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full truncate max-w-[150px]">
                    {data.tenant.name}
                  </span>
                )}
              </div>
              <p className="text-slate-500 text-xs sm:text-sm hidden sm:block">
                Manage your account, infrastructure, and team preferences
              </p>
            </div>
            
            <AccountHealthRing 
              percentage={health.percentage} 
              actionsNeeded={health.actionsNeeded} 
            />
          </div>
          
          {/* Quick Stats Bar */}
          <div className="mt-4 sm:mt-6">
            <QuickStatsBar 
              stats={{
                phoneNumbers: data.phones?.total || 0,
                teamMembers: data.team?.total || 0,
                a2pStatus: data.a2p?.status || 'not_started'
              }}
            />
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-2 sm:space-y-3">
          {sections.filter((section) => !section.globalAdminOnly || isGlobalAdmin).map((section) => {
            const SectionComponent = section.component;
            
            return (
            <SettingsSection
              key={section.id}
              id={section.id}
              icon={section.icon}
              iconGradient={section.iconGradient}
              title={section.title}
              status={section.status}
              statusText={section.statusText}
              summary={section.summary}
              lastUpdated={section.lastUpdated}
              quickAction={section.quickAction}
              progress={section.progress}
              avatars={section.avatars}
              isExpanded={expandedSection === section.id}
              onToggle={() => toggleSection(section.id)}
              tourClass={section.tourClass}
            >
                <SectionComponent onUpdate={refetch} />
              </SettingsSection>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-6 sm:mt-10 text-center">
          <p className="text-xs sm:text-sm text-slate-400">
            Need help?{' '}
            <a href="#" className="text-orange-600 hover:text-orange-700 hover:underline">
              Contact support
            </a>
            {' · '}
            <a href="#" className="text-slate-500 hover:text-slate-600 hover:underline">
              Docs
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;